/**
 * GraphQL API Service - Amplify Data Integration
 * This service uses the auto-generated GraphQL API from Amplify Data
 */

import { generateClient } from 'aws-amplify/data';

// Import outputs to ensure they're available
let amplifyOutputs = null;
try {
  amplifyOutputs = require('../amplify_outputs.json');
} catch (e) {
  console.warn('amplify_outputs.json not found in GraphQL service');
}

// Lazy-load the client to ensure Amplify is configured first
let client = null;
let clientError = null;

const getClient = () => {
  if (clientError) {
    throw clientError;
  }
  
  if (!client) {
    try {
      // For Amplify Gen 2, generateClient() automatically reads from Amplify.configure() outputs
      // No parameters needed - it will use the Data API configuration from Amplify.configure()
      // This is the recommended approach for Amplify Gen 2
      client = generateClient();
      
      // Verify client has models available
      if (!client) {
        throw new Error('GraphQL client is null. Ensure Data API is configured.');
      }
      
      // Wait a moment for models to be available, then verify
      if (!client.models) {
        // Models might not be immediately available, but they should be
        console.warn('⚠️ GraphQL client models not immediately available');
        // Don't throw error yet - might be a timing issue
      } else {
        console.log('✅ GraphQL client initialized successfully with models:', Object.keys(client.models || {}));
      }
      
    } catch (error) {
      console.error('❌ Failed to generate GraphQL client:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      
      if (error.message && (error.message.includes('GraphQL endpoint') || error.message.includes('not been configured'))) {
        console.error('⚠️ Data API not configured. Please ensure:');
        console.error('   1. Backend is deployed: npm run sandbox');
        console.error('   2. amplify_outputs.json exists in src/ with data section');
        console.error('   3. Amplify.configure() was called with outputs in src/index.js');
        console.error('   4. amplify_outputs.json contains model_introspection');
        
        if (amplifyOutputs) {
          console.error('   Current outputs:', {
            hasAuth: !!amplifyOutputs.auth,
            hasData: !!amplifyOutputs.data,
            hasModelIntrospection: !!(amplifyOutputs.data && amplifyOutputs.data.model_introspection),
            dataUrl: amplifyOutputs.data?.url
          });
        }
      }
      
      const friendlyError = new Error(
        'Backend API is not deployed yet. To use league features, you need to deploy the backend with: npm run sandbox'
      );
      friendlyError.needsBackendDeploy = true;
      friendlyError.originalError = error;
      clientError = friendlyError;
      throw friendlyError;
    }
  }
  return client;
};

class GraphQLApiService {
  // ==================== League APIs ====================

  /**
   * Create a new league
   */
  async createLeague(leagueData) {
    try {
      const client = getClient();
      const leagueId = `league-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const teamId = `team-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Get current MLB season for defaults
      const { getCurrentSeason } = await import('../utils/seasonManager');
      const currentSeason = getCurrentSeason();
      
      // Prepare data - JSON fields need to be stringified
      const settingsObj = {
        maxTeams: leagueData.settings?.maxTeams || 12,
        scoringType: leagueData.settings?.scoringType || 'Standard',
        rosterSize: leagueData.settings?.rosterSize || 40
      };

      const seasonObj = {
        startDate: leagueData.season?.startDate || currentSeason.startDate,
        endDate: leagueData.season?.endDate || currentSeason.endDate,
        year: leagueData.season?.year || currentSeason.year
      };

      const teamName = leagueData.teamName || "My Team";
      const userId = leagueData.userId || leagueData.creatorId;
      const teamsArray = [
        {
          id: teamId,
          name: teamName,
          acronym: leagueData.teamAbbreviation || teamName.substring(0, 4).toUpperCase() || 'TEAM',
          owner: userId,
          ownerName: leagueData.userName || leagueData.userFullName || 'Owner',
          logoEmoji: leagueData.logoEmoji || '⚾',
          logoColor: leagueData.logoColor || '#007bff',
          points: 0
        }
      ];

      // settings/season/teams are String fields in the schema (see updateLeague,
      // which stringifies the same three fields, and getLeagues, which JSON.parses
      // league.teams back) — must be JSON-stringified, not passed as raw objects.
      const { data, errors } = await client.models.League.create({
        leagueId,
        name: leagueData.name,
        description: leagueData.description || '',
        creatorId: userId,
        settings: JSON.stringify(settingsObj),
        season: JSON.stringify(seasonObj),
        teams: JSON.stringify(teamsArray),
        draftDate: leagueData.draftDate || null,
        draftTime: leagueData.draftTime || null,
        isPrivate: leagueData.isPrivate || false,
        password: leagueData.password ?? null,
        status: leagueData.status || 'Draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to create league');
      }

      return { success: true, league: data };
    } catch (error) {
      console.error('Error creating league:', error);
      throw error;
    }
  }

  /**
   * Get all leagues for a user
   */
  async getLeagues(userId) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.League.list({
        filter: {
          or: [
            { creatorId: { eq: userId } },
            // Note: Filtering by nested array fields requires custom logic
          ]
        }
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch leagues');
      }

      // Additional client-side filtering for teams
      const userLeagues = data.filter(league => {
        if (league.creatorId === userId) return true;
        if (league.teams) {
          const teamsArray = JSON.parse(league.teams);
          const userTeam = teamsArray.find(team => team.owner === userId);
          
          // Include league if user has a team with status 'active' or undefined (backward compatibility)
          if (userTeam) {
            console.log(`Found user team in league ${league.name}:`, userTeam);
            return userTeam.status !== 'invited' && userTeam.status !== 'declined';
          }
        }
        return false;
      });

      console.log(`Found ${userLeagues.length} leagues for user ${userId}`);
      return { success: true, leagues: userLeagues, count: userLeagues.length };
    } catch (error) {
      console.error('Error fetching leagues:', error);
      throw error;
    }
  }

  /**
   * Get all leagues (for finding pending invites)
   */
  async getAllLeagues() {
    try {
      const client = getClient();
      
      // Check if the client and models are properly initialized
      if (!client || !client.models || !client.models.League) {
        throw new Error('GraphQL client not properly initialized. Please ensure you are authenticated.');
      }
      
      const { data, errors } = await client.models.League.list();

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch all leagues');
      }

      return { success: true, leagues: data || [], count: data?.length || 0 };
    } catch (error) {
      console.error('Error fetching all leagues:', error);
      throw error;
    }
  }

  /**
   * Get a specific league
   */
  async getLeague(leagueId) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.League.get({ leagueId });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch league');
      }

      return { success: true, league: data };
    } catch (error) {
      console.error('Error fetching league:', error);
      throw error;
    }
  }

  /**
   * Update league settings
   */
  async updateLeague(leagueId, updates) {
    try {
      const client = getClient();
      
      // Stringify JSON fields if they're objects
      const processedUpdates = { ...updates };
      if (processedUpdates.settings && typeof processedUpdates.settings === 'object') {
        processedUpdates.settings = JSON.stringify(processedUpdates.settings);
      }
      if (processedUpdates.season && typeof processedUpdates.season === 'object') {
        processedUpdates.season = JSON.stringify(processedUpdates.season);
      }
      if (processedUpdates.teams && typeof processedUpdates.teams === 'object') {
        processedUpdates.teams = JSON.stringify(processedUpdates.teams);
      }
      
      const { data, errors } = await client.models.League.update({
        leagueId,
        ...processedUpdates,
        updatedAt: new Date().toISOString()
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to update league');
      }

      return { success: true, league: data };
    } catch (error) {
      console.error('Error updating league:', error);
      throw error;
    }
  }

  /**
   * Delete a league
   */
  async deleteLeague(leagueId) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.League.delete({ leagueId });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to delete league');
      }

      return { success: true, league: data };
    } catch (error) {
      console.error('Error deleting league:', error);
      throw error;
    }
  }

  // ==================== User APIs ====================

  /**
   * Create or update user profile
   */
  async createUser(userData) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.User.create({
        userId: userData.userId,
        email: userData.email,
        name: userData.name,
        leagues: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to create user');
      }

      return { success: true, user: data };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * Get user profile
   */
  async getUser(userId) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.User.get({ userId });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch user');
      }

      return { success: true, user: data };
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  // ==================== Roster APIs (period-based: leagueId, teamId, periodKey) ====================

  /**
   * Get roster for a team/period. Use rosterService.getRoster for full support.
   */
  async getRoster(leagueId, teamId, periodKey) {
    try {
      const client = getClient();
      if (!client.models?.Roster) throw new Error('Roster model not available');
      const { data, errors } = await client.models.Roster.get({ leagueId, teamId, periodKey });
      if (errors?.length) throw new Error(errors[0]?.message || 'Failed to fetch roster');
      return { success: true, roster: data };
    } catch (error) {
      console.error('Error fetching roster:', error);
      throw error;
    }
  }

  /**
   * Update roster. Use rosterService.saveRoster for create-or-update.
   */
  async updateRoster(leagueId, teamId, periodKey, periodType, roster) {
    try {
      const client = getClient();
      if (!client.models?.Roster) throw new Error('Roster model not available');
      const { data, errors } = await client.models.Roster.update({
        leagueId,
        teamId,
        periodKey,
        periodType: periodType || 'daily',
        players: roster.players ?? [],
        bench: roster.bench ?? [],
        minorLeague: roster.minorLeague ?? [],
        activeLineup: roster.activeLineup ?? {},
        lineupsByDate: roster.lineupsByDate ?? null,
        lastUpdated: new Date().toISOString()
      });
      if (errors?.length) throw new Error(errors[0]?.message || 'Failed to update roster');
      return { success: true, roster: data };
    } catch (error) {
      console.error('Error updating roster:', error);
      throw error;
    }
  }

  /**
   * Create roster. Use rosterService.saveRoster for create-or-update.
   */
  async createRoster(leagueId, teamId, periodKey, periodType, roster = {}) {
    try {
      const client = getClient();
      if (!client.models?.Roster) throw new Error('Roster model not available');
      const { data, errors } = await client.models.Roster.create({
        leagueId,
        teamId,
        periodKey,
        periodType: periodType || 'daily',
        players: roster.players || [],
        bench: roster.bench || [],
        minorLeague: roster.minorLeague || [],
        activeLineup: roster.activeLineup || {},
        lineupsByDate: roster.lineupsByDate || null,
        lastUpdated: new Date().toISOString()
      });
      if (errors?.length) throw new Error(errors[0]?.message || 'Failed to create roster');
      return { success: true, roster: data };
    } catch (error) {
      console.error('Error creating roster:', error);
      throw error;
    }
  }

  // ==================== Player APIs ====================

  /**
   * Get all available players
   */
  async getPlayers(filters = {}) {
    try {
      const client = getClient();
      const filterObj = {};
      if (filters.position) {
        filterObj.position = { eq: filters.position };
      }
      if (filters.team) {
        filterObj.team = { eq: filters.team };
      }

      const { data, errors } = await client.models.Player.list({
        filter: Object.keys(filterObj).length > 0 ? filterObj : undefined
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch players');
      }

      return { success: true, players: data, count: data.length };
    } catch (error) {
      console.error('Error fetching players:', error);
      throw error;
    }
  }

  /**
   * Get a page of players from the DB (server-side limit).
   * Returns { success, players, nextToken, count }.
   */
  async getPlayersPage(options = {}) {
    try {
      const client = getClient();
      const { limit = 50, nextToken, filters = {} } = options;
      const filterObj = {};
      if (filters.position) {
        filterObj.position = { eq: filters.position };
      }
      if (filters.team) {
        filterObj.team = { eq: filters.team };
      }
      // Note: deployed Player model has no top-level 'status' field; filter client-side if needed

      const { data, nextToken: next, errors } = await client.models.Player.list({
        limit: Math.min(limit, 100),
        nextToken: nextToken || undefined,
        filter: Object.keys(filterObj).length > 0 ? filterObj : undefined
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch players');
      }

      return {
        success: true,
        players: data || [],
        nextToken: next || null,
        count: (data || []).length
      };
    } catch (error) {
      console.error('Error fetching players page:', error);
      throw error;
    }
  }

  /**
   * Get a specific player
   */
  async getPlayer(playerId) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.Player.get({ playerId });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to fetch player');
      }

      return { success: true, player: data };
    } catch (error) {
      console.error('Error fetching player:', error);
      throw error;
    }
  }

  /**
   * Search players by name
   */
  async searchPlayers(query) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.Player.list({
        filter: {
          name: { contains: query }
        }
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to search players');
      }

      return { success: true, players: data, count: data.length };
    } catch (error) {
      console.error('Error searching players:', error);
      throw error;
    }
  }

  /**
   * Create or update player
   */
  async createPlayer(playerData) {
    try {
      const client = getClient();
      const { data, errors } = await client.models.Player.create({
        playerId: playerData.playerId,
        name: playerData.name,
        team: playerData.team,
        position: playerData.position,
        stats: playerData.stats || {},
        updatedAt: new Date().toISOString()
      });

      if (errors) {
        throw new Error(errors[0]?.message || 'Failed to create player');
      }

      return { success: true, player: data };
    } catch (error) {
      console.error('Error creating player:', error);
      throw error;
    }
  }

  // ==================== Real-time Subscriptions ====================

  /**
   * Subscribe to league updates
   */
  subscribeToLeague(leagueId, callback) {
    const client = getClient();
    const subscription = client.models.League.observeById({ leagueId }).subscribe({
      next: (data) => callback(data),
      error: (error) => console.error('Subscription error:', error)
    });

    return () => subscription.unsubscribe();
  }

  /**
   * Subscribe to roster updates for a team/period.
   */
  subscribeToRoster(leagueId, teamId, periodKey, callback) {
    const client = getClient();
    if (!client.models?.Roster) return () => {};
    const sub = client.models.Roster.observeById({ leagueId, teamId, periodKey }).subscribe({
      next: (data) => callback(data),
      error: (e) => console.error('Roster subscription error:', e)
    });
    return () => sub.unsubscribe();
  }
}

// Export singleton instance
const apiService = new GraphQLApiService();
export default apiService;

